import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { programs } from '../../data/programs';
import { getProgress } from '../../lib/storage/progressStore';
import { track } from '../../lib/analytics/track';
import Navbar from '../../components/Navbar';
import SEOHead from '../../components/SEOHead';
import { ChevronRight } from 'lucide-react';

export default function ProgramsIndex() {
  useEffect(() => {
    track('programs_index_view');
  }, []);

  // Schema.org para Google Rich Snippets
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "name": "Breadcrumbs",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": "https://edutechpulselab.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Micro-Programas",
            "item": "https://edutechpulselab.com/programas"
          }
        ]
      },
      {
        "@type": "ItemList",
        "name": "Micro-Programas Ejecutivos",
        "description": "Formación práctica en audio diseñada para ejecutivos. Aprende metodologías consultivas aplicadas.",
        "itemListElement": programs.map((program, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": program.title,
            "description": program.outcome,
            "provider": {
              "@type": "EducationalOrganization",
              "name": "EduTech Pulse Lab",
              "url": "https://edutechpulselab.com"
            },
            "educationalLevel": "Executive",
            "timeRequired": `PT${program.durationMin}M`,
            "numberOfCredits": program.units.length,
            "inLanguage": "es-ES",
            "url": `https://edutechpulselab.com/p/${program.slug}`,
            ...(program.sources && {
              "isBasedOn": program.sources.map(source => ({
                "@type": "Book",
                "name": source.book,
                "author": {
                  "@type": "Person",
                  "name": source.author,
                  "affiliation": {
                    "@type": "Organization",
                    "name": source.institution
                  }
                }
              }))
            }),
            "hasCourseInstance": {
              "@type": "CourseInstance",
              "courseMode": "online",
              "courseWorkload": `PT${program.durationMin}M`
            }
          }
        }))
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Briefings Ejecutivos | Edutech Pulse Lab"
        description="Audios breves que preparan tu criterio antes de entrar a un escenario, conversación difícil o simulación con IA."
        canonicalUrl="/programas"
        ogType="website"
        schema={schema}
        robots="index, follow, max-image-preview:large, max-snippet:-1"
      />
      <Navbar />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
        {/* Hero - Optimizado móvil: 30% viewport */}
        <section className="relative bg-gradient-to-br from-[#0c203b] via-[#1a365d] to-[#2d4a6b] text-white py-8 sm:py-16 lg:py-20 overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
            <div className="max-w-3xl">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-2 sm:mb-6 tracking-tight leading-[1.1]">
                Briefings ejecutivos
              </h1>
              <p className="text-sm sm:text-2xl text-slate-300 mb-3 sm:mb-8 leading-relaxed font-light tracking-wide">
                Audios breves que preparan tu criterio antes de entrar a un escenario, conversación difícil o simulación con IA.
              </p>
            </div>
          </div>
        </section>

        {/* Lista de programas */}
        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12 md:py-16">
          <div className="grid gap-4 sm:gap-6">
            {programs.map((program) => {
              const progress = getProgress(program.slug);
              const completedUnits = Object.keys(progress).filter(
                (k) => progress[k]?.completedAt
              ).length;
              const totalUnits = program.units.length;
              const progressPercent = (completedUnits / totalUnits) * 100;
              const isCompleted = completedUnits === totalUnits && totalUnits > 0;
              const hasProgress = completedUnits > 0 && !isCompleted;

              return (
                <div
                  key={program.slug}
                  className="block rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-slate-100 overflow-hidden relative bg-white"
                >
                  {/* Barra multifunción: decorativa cyan o progreso cyan */}
                  {completedUnits > 0 ? (
                    <div className="h-1 bg-cyan-100">
                      <div 
                        className="h-full bg-cyan-600 transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  ) : (
                    <div className="h-1 bg-cyan-600"></div>
                  )}
                  
                  <div className="relative px-5 sm:px-8 md:px-10 pt-5 sm:pt-8 md:pt-10 pb-5 sm:pb-6 md:pb-8">
                    {/* 1. IDENTIDAD (sello editorial) */}
                    <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3 sm:mb-6">
                      Briefing ejecutivo {program.programType || 'editorial'}
                    </p>
                    
                    {/* Marca distintiva: línea sutil */}
                    <div className="w-12 h-0.5 bg-cyan-600 mb-3 sm:mb-6"></div>
                    
                    {/* 2. TÍTULO (protagonista) */}
                    <h2 className="text-xl sm:text-3xl md:text-4xl font-semibold text-slate-900 mb-3 sm:mb-6 tracking-tight leading-tight">
                      {program.title}
                    </h2>
                    
                    {/* 3. FRASE EDITORIAL (tesis) */}
                    <p className="text-base sm:text-xl md:text-2xl font-semibold text-slate-800 mb-3 sm:mb-8 leading-snug sm:leading-relaxed">
                      {program.tagline}
                    </p>
                    
                    {/* 4. PROPUESTA (afilada) */}
                    <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-5 sm:mb-10">
                      {program.description}
                    </p>
                    
                    {/* 5. FICHA TÉCNICA + CTA */}
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between pt-4 sm:pt-6 border-t border-slate-100 gap-3 sm:gap-0">
                      <div className="space-y-1 sm:space-y-1.5 text-xs text-slate-500 leading-snug">
                        <p>{program.durationMin} min · {totalUnits} {totalUnits === 1 ? 'conversación' : 'conversaciones'}</p>
                        <p>{program.audienceDescription}</p>
                        {program.sources && program.sources.length > 0 && (
                          <p className="pt-1">
                            {program.sourcesIntro}: {program.sources.map(s => s.book).join(' · ')}
                          </p>
                        )}
                      </div>
                      
                      <Link
                        to={`/p/${program.slug}`}
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-cyan-600 text-cyan-600 hover:bg-cyan-600/10 font-semibold text-sm transition-colors self-end sm:self-auto sm:ml-6 sm:-mb-1"
                      >
                        {program.ctaLabel || 'Explorar'}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </>
  );
}
